import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Clock3, DoorOpen, Info, Menu, Search, Users, X } from "lucide-react";
import { rooms } from "../shared/rooms.js";
import { buildDemoCache } from "../shared/demo.js";
import { addDays, availableRooms, dayNames, displayText, eventKind, formatShortDate, formatWeekPeriod, isoDate, slotSpanForEvent, slots, timeChoices, weekStart } from "../shared/schedule.js";
import { DEFAULT_UPDATE_TIMES, nextRefreshAt } from "../shared/refresh.js";
const NAV_ITEMS = [
  { id: "schedule", label: "Quadro semanal", icon: CalendarDays },
  { id: "availability", label: "Consultar disponibilidade", icon: Search },
];

function formatUpdated(value) {
  if (!value) return "Aguardando atualização";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function Header({ onMenu, mobileOpen, updatedAt, source }) {
  return (
    <header className="topbar">
      <button className="icon-button mobile-menu" onClick={onMenu} aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}>{mobileOpen ? <X size={22} /> : <Menu size={22} />}</button>
      <a className="brand" href="https://efi.prograd.ufsc.br" target="_blank" rel="noreferrer" aria-label="Espaço Físico Integrado da UFSC">
        <img className="ufsc-signature" src="/brand/ufsc-horizontal-fundo-escuro.png" alt="Universidade Federal de Santa Catarina" />
        <span className="brand-divider" aria-hidden="true" />
        <span className="brand-copy"><strong>Espaço Físico Integrado</strong><small>Quadro de horários</small></span>
      </a>
      <div className="topbar-status">
        <span className="status-dot" aria-hidden="true" />
        <span><small>{source === "sief" ? "Dados do SIEF" : "Demonstração local"}</small><strong>{formatUpdated(updatedAt)}</strong></span>
      </div>
    </header>
  );
}

function Sidebar({ page, setPage, open, close }) {
  return (
    <>
      <button className={`sidebar-scrim ${open ? "visible" : ""}`} aria-label="Fechar menu" onClick={close} />
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <nav aria-label="Navegação principal">
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => <button key={id} className={page === id ? "active" : ""} onClick={() => { setPage(id); close(); }}><Icon size={19} /><span>{label}</span></button>)}
        </nav>
        <div className="sidebar-note"><Info size={17} /><p>Consulte os horários antes de solicitar uma reserva à Secretaria do EFI.</p></div>
        <a className="site-link" href="https://efi.prograd.ufsc.br" target="_blank" rel="noreferrer">Acessar o site do EFI</a>
      </aside>
    </>
  );
}

function RoomTabs({ selected, setSelected }) {
  const strip = useRef(null);
  const move = (direction) => strip.current?.scrollBy({ left: direction * 360, behavior: "smooth" });
  return <div className="room-tabs-wrap"><button className="tab-scroll-button" onClick={() => move(-1)} aria-label="Ver salas anteriores"><ChevronLeft size={16} /></button><div ref={strip} className="room-tabs" role="tablist" aria-label="Ambientes do EFI">{rooms.map((room) => <button key={room.sigla} role="tab" aria-selected={selected === room.sigla} className={selected === room.sigla ? "selected" : ""} onClick={(event) => { setSelected(room.sigla); event.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}><strong>{room.sigla}</strong></button>)}</div><button className="tab-scroll-button" onClick={() => move(1)} aria-label="Ver próximas salas"><ChevronRight size={16} /></button></div>;
}

function WeekNavigation({ monday, setMonday, room }) {
  return <div className="week-nav"><div className="week-room-info"><strong>{room.nome}</strong><span>{room.capacidade} lugares</span></div><div className="week-controls"><button className="secondary-button" onClick={() => setMonday(addDays(monday, -7))} aria-label="Semana anterior"><ChevronLeft size={18} /></button><div><small>Semana</small><strong>{formatWeekPeriod(monday)}</strong></div><button className="secondary-button" onClick={() => setMonday(addDays(monday, 7))} aria-label="Próxima semana"><ChevronRight size={18} /></button><button className="today-button" onClick={() => setMonday(weekStart(new Date()))}>Hoje</button></div><div className="week-actions"><div className="legend" aria-label="Legenda"><span><i className="class-dot" />Aula</span><span><i className="reservation-dot" />Reserva</span><span><i className="free-dot" />Livre</span></div></div></div>;
}

function cellState(events, day, rowIndex) {
  for (const event of events) {
    const span = slotSpanForEvent(event, day);
    if (!span || rowIndex < span.start || rowIndex >= span.start + span.span) continue;
    return { event, span, startsHere: span.start === rowIndex };
  }
  return null;
}

function ScheduleGrid({ events, room, monday }) {
  const days = dayNames.map((name, index) => ({ name, date: addDays(monday, index) }));
  return (
    <div className="schedule-scroll" tabIndex="0" aria-label="Quadro de horários com rolagem horizontal">
      <table className="schedule-table">
        <thead><tr><th className="time-header">Horário</th>{days.map((day) => <th key={day.name}><span>{day.name}</span><small>{formatShortDate(day.date)}</small></th>)}</tr></thead>
        <tbody>{slots.map((slot, rowIndex) => {
          const periodStart = rowIndex === 5 || rowIndex === 10;
          return <tr key={`${slot.start}-${slot.end}`} className={periodStart ? "period-start" : ""}>
            <th scope="row" className="time-cell">{periodStart && <span className="period-label">{slot.turn}</span>}<strong>{slot.start}</strong><small>{slot.end}</small></th>
            {days.map((day) => {
              const state = cellState(events, day.date, rowIndex);
              if (state && !state.startsHere) return null;
              if (!state) return <td className="empty-slot" key={day.name} aria-label={`${day.name}, ${slot.start}: livre`} />;
              const kind = eventKind(state.event);
              return <td key={day.name} rowSpan={state.span.span} className={`event-slot ${kind}`}><div className="event-card"><strong>{displayText(state.event, room.sigla)}</strong><span><Clock3 size={13} /> {state.event.start_time}–{state.event.end_time}</span></div></td>;
            })}
          </tr>;
        })}</tbody>
      </table>
    </div>
  );
}

function SchedulePage({ cache }) {
  const [selected, setSelected] = useState("EFI201");
  const [monday, setMonday] = useState(() => weekStart(new Date()));
  const room = rooms.find((item) => item.sigla === selected) || rooms[0];
  const events = cache.events?.[room.sigla] || [];
  return <>
    <h1 className="sr-only">Quadro semanal de horários</h1>
    <div className="schedule-workspace"><RoomTabs selected={selected} setSelected={setSelected} />
      <section className="schedule-panel" aria-label={`Horários de ${room.nome}`}><WeekNavigation monday={monday} setMonday={setMonday} room={room} /><ScheduleGrid events={events} room={room} monday={monday} /></section>
    </div>
  </>;
}

function AvailabilityPage({ cache }) {
  const [date, setDate] = useState(isoDate(new Date()));
  const [startTime, setStartTime] = useState("13:30");
  const [endTime, setEndTime] = useState("15:10");
  const [roomType, setRoomType] = useState("Todos");
  const [capacity, setCapacity] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const error = endTime <= startTime ? "O horário final deve ser posterior ao horário inicial." : "";
  const result = useMemo(() => submitted && !error ? availableRooms(rooms, cache.events || {}, new Date(`${date}T12:00:00`), startTime, endTime, roomType, Number(capacity || 0)) : [], [submitted, error, date, startTime, endTime, roomType, capacity, cache]);
  const alter = (setter) => (event) => { setter(event.target.value); setSubmitted(false); };
  return <>
    <div className="page-heading"><div><span className="eyebrow">Planeje sua atividade</span><h1>Consultar disponibilidade</h1><p>Encontre ambientes livres em uma data e horário específicos.</p></div></div>
    <section className="form-panel"><form className="filter-form" onSubmit={(event) => { event.preventDefault(); setSubmitted(true); }}>
      <label>Data<input type="date" value={date} onChange={alter(setDate)} required /></label>
      <label>Início<select value={startTime} onChange={alter(setStartTime)}>{timeChoices.map((time) => <option key={time}>{time}</option>)}</select></label>
      <label>Fim<select value={endTime} onChange={alter(setEndTime)}>{timeChoices.map((time) => <option key={time}>{time}</option>)}</select></label>
      <label>Tipo<select value={roomType} onChange={alter(setRoomType)}><option>Todos</option><option>Sala de aula</option><option>Auditório</option></select></label>
      <label>Capacidade mínima<input type="number" min="0" max="180" value={capacity} onChange={alter(setCapacity)} /></label>
      <button className="primary-button" type="submit"><Search size={18} />Buscar</button>
    </form>{submitted && error && <p className="form-error" role="alert">{error}</p>}</section>
    {submitted && !error && <section className="results-section" aria-live="polite"><div className="results-heading"><h2>{result.length} {result.length === 1 ? "ambiente disponível" : "ambientes disponíveis"}</h2><p>{date.split("-").reverse().join("/")} · {startTime} às {endTime}</p></div><div className="room-results">{result.map((room) => <article key={room.sigla} className="room-result"><div className="room-icon"><DoorOpen size={21} /></div><div><strong>{room.sigla}</strong><span>{room.nome} · {room.pavimento}º pavimento</span></div><span className="capacity"><Users size={15} /> {room.capacidade}</span></article>)}</div></section>}
  </>;
}

export default function App() {
  const [page, setPage] = useState("schedule");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cache, setCache] = useState(() => buildDemoCache());

  const loadSchedule = useCallback(async () => {
    try {
      const response = await fetch("/api/schedule", { headers: { accept: "application/json" } });
      if (!response.ok) throw new Error("Agenda indisponível");
      setCache(await response.json());
    } catch {
      setCache((current) => current?.events ? current : buildDemoCache());
    }
  }, []);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const updateTimeKey = (Array.isArray(cache.update_times) && cache.update_times.length ? cache.update_times : DEFAULT_UPDATE_TIMES).join(",");

  useEffect(() => {
    const updateTimes = updateTimeKey.split(",");
    let timeout;
    let active = true;

    function scheduleNextRefresh() {
      if (!active) return;
      const delay = Math.max(1_000, nextRefreshAt(updateTimes).getTime() - Date.now());
      timeout = window.setTimeout(async () => {
        await loadSchedule();
        scheduleNextRefresh();
      }, delay);
    }

    scheduleNextRefresh();
    return () => { active = false; window.clearTimeout(timeout); };
  }, [loadSchedule, updateTimeKey]);

  return <div className="app-shell"><Header onMenu={() => setMobileOpen((value) => !value)} mobileOpen={mobileOpen} updatedAt={cache.updated_at} source={cache.source} /><Sidebar page={page} setPage={setPage} open={mobileOpen} close={() => setMobileOpen(false)} /><main className={`main-content ${page === "schedule" ? "schedule-page" : ""}`}>{page === "schedule" && <SchedulePage cache={cache} />}{page === "availability" && <AvailabilityPage cache={cache} />}<footer>Universidade Federal de Santa Catarina · Espaço Físico Integrado</footer></main></div>;
}
